'use client';

import { useState, useEffect, useRef, useCallback } from 'react';

/**
 * Returns true when the user has scrolled past a given percentage of the page.
 *
 * Uses a passive scroll listener with requestAnimationFrame throttling
 * to avoid layout thrashing.
 *
 * @param threshold - Scroll percentage (0-100) at which to trigger
 * @returns Whether the scroll position is at or beyond the threshold
 */
export function useScrollThreshold(threshold: number): boolean {
  const [isPast, setIsPast] = useState(false);
  const rafId = useRef(0);

  const handleScroll = useCallback(() => {
    if (rafId.current) return;

    rafId.current = requestAnimationFrame(() => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      if (scrollHeight <= 0) {
        rafId.current = 0;
        return;
      }

      const scrollPercent = (window.scrollY / scrollHeight) * 100;
      setIsPast(scrollPercent >= threshold);
      rafId.current = 0;
    });
  }, [threshold]);

  useEffect(() => {
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafId.current) cancelAnimationFrame(rafId.current);
    };
  }, [handleScroll]);

  return isPast;
}
